import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { compose } from 'recompose';
import {
  Modal, Form, Input, Checkbox, Icon, Message, Label, Loader, Button, Progress,
} from 'semantic-ui-react';

import hasRESTClient from '../../hocs/has-rest-client';
import { authService } from '../../utils/auth-service';
import { getInfluencerFromOrganisations } from '../../utils/helper-functions';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = e => typeof e === 'string' && EMAIL_REGEX.test(_.trim(e));

// Status truth comes from the dyzio API, never from the send service: a
// recipient is only "sent" once the discussion actually carries a code.
const SENT_STATUS = 'DISCOUNT_SENT';
const isDiscussionSent = source =>
  !!_.get(source, 'internalDiscountCode') || _.get(source, 'workflowStatus') === SENT_STATUS;

// discussionId -> { sent, code }. Works for both the GraphQL discussion prop
// (fields at top level) and JSON:API rows (fields under attributes) — pass the
// object that actually holds internalDiscountCode/workflowStatus.
const buildStatusById = (items, sourceOf) => {
  const statusById = {};
  items.forEach(item => {
    const source = sourceOf(item);
    statusById[item.id] = { sent: isDiscussionSent(source), code: _.get(source, 'internalDiscountCode', null) };
  });
  return statusById;
};

// Poll the API after a submit until every in-flight recipient shows a code (or
// we give up and tell the user to check back).
const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 20;

const getRecipient = discussion => {
  const influencer = discussion.influencer
    || getInfluencerFromOrganisations(discussion.organisations);
  return {
    discussionId: discussion.id,
    influencerId: _.get(influencer, 'id', null),
    name: discussion.name || _.get(influencer, 'name', ''),
    email: _.get(influencer, 'email', ''),
  };
};

// One recipient's picture: API status merged with the local just-submitted batch.
const computeRowState = (recipient, statusById, submittedSet, sendPhase) => {
  const status = statusById[recipient.discussionId] || {};
  const sent = !!status.sent;
  const inBatch = submittedSet.has(recipient.discussionId);
  return {
    sent,
    code: status.code,
    sending: !sent && inBatch && sendPhase === 'sending',
    unresolved: !sent && inBatch && sendPhase === 'timedout',
    sendable: isValidEmail(recipient.email) && !!recipient.influencerId,
  };
};

const tabStyle = isActive => (isActive ? {
  backgroundColor: '#fff5f8',
  color: '#ed1f7e',
  boxShadow: '0 0 0 1px #ed1f7e inset',
  fontWeight: 600,
} : null);

class SendDiscountCodesModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoadingCount: false,
      availableCount: null,
      countError: null,
      // Which Mandrill template recipients receive — from /discount-codes/access.
      templateName: null,
      // Seeded from the campaign we already hold (GraphQL discussions carry the
      // discount fields) — no status request on open. Refreshed by polling
      // only after a send.
      statusById: buildStatusById(props.discussions, d => d),
      selectedDiscussionIds: [],
      // 'tosend' | 'sent'
      activeTab: 'tosend',
      filterTerm: '',
      isSubmitting: false,
      confirmingSend: false,
      submitError: null,
      // Test send — a preview of the email to yourself (placeholder code).
      testEmail: props.currentUserEmail || '',
      isSendingTest: false,
      testResult: null,
      // The batch we last submitted — drives the "Sending…" state until the
      // API confirms each one.
      submittedIds: [],
      // 'idle' | 'sending' | 'timedout'
      sendPhase: 'idle',
    };
    this._isMounted = false;
    this._sendingTest = false;
    this._pollTimer = null;
    this._pollCount = 0;
    this._submitting = false;
  }

  componentDidMount() {
    this._isMounted = true;
    if (this.props.productCode) {
      this.fetchAvailableCount();
      this.fetchAccess();
    }
  }

  // Fetch the org's discount template name so the user can see which email
  // recipients receive. Non-fatal — silently no-ops if unavailable.
  fetchAccess = async () => {
    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    if (!url) return;
    try {
      const token = authService.getToken();
      const response = await axios.get(`${url}/discount-codes/access`, {
        timeout: 15000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const templateName = _.get(response, 'data.data.templateName', null);
      if (this._isMounted && templateName) this.setState({ templateName });
    } catch (err) {
      // Leave templateName as-is — just don't show it.
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.stopPolling();
  }

  fetchAvailableCount = async () => {
    const { restClient, productCode } = this.props;
    this.setState({ isLoadingCount: true, countError: null });
    try {
      const response = await restClient.get(
        `/discounts?filter[product]=${encodeURIComponent(productCode)}&filter[allocated]=false&page[limit]=1`,
      );
      // API omits meta.page entirely when there are 0 matches — coalesce to 0.
      const availableCount = _.get(response, 'data.meta.page.total', 0);
      if (this._isMounted) this.setState({ availableCount, isLoadingCount: false });
    } catch (err) {
      if (this._isMounted) this.setState({ countError: _.get(err, 'message', 'Failed to load codes'), isLoadingCount: false });
    }
  }

  // Authoritative sent/sending picture — reads discount state per discussion
  // from the dyzio API. Sparse fieldset keeps the payload small enough to poll.
  fetchStatus = async () => {
    const { restClient, campaignId } = this.props;
    try {
      const response = await restClient.get(
        `/discussions?filter[campaign]=${campaignId}&fields[discussions]=internalDiscountCode,workflowStatus&page[limit]=500`,
      );
      const rows = _.get(response, 'data.data', []);
      if (this._isMounted) this.setState({ statusById: buildStatusById(rows, d => d.attributes) });
    } catch (err) {
      // Non-fatal — the picker still works, we just can't refresh sent badges.
      console.error('[discount-codes] failed to load discount status', err); // eslint-disable-line no-console
    }
  }

  stopPolling = () => {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  startPolling = () => {
    this.stopPolling();
    this._pollCount = 0;
    this.setState({ sendPhase: 'sending' });
    this._pollTimer = setInterval(async () => {
      this._pollCount += 1;
      await this.fetchStatus();
      if (!this._isMounted) return;
      const stillSending = this.state.submittedIds.filter(id => !_.get(this.state.statusById, [id, 'sent']));
      if (stillSending.length === 0 || this._pollCount >= MAX_POLLS) {
        this.stopPolling();
        // Codes were consumed — refresh the count once now that we're done.
        this.fetchAvailableCount();
        this.setState({ sendPhase: stillSending.length === 0 ? 'idle' : 'timedout' });
      }
    }, POLL_INTERVAL_MS);
  }

  setTab = tab => () => this.setState({ activeTab: tab, filterTerm: '' });

  onFilterChange = (e, { value }) => this.setState({ filterTerm: value });

  toggleDiscussionId = id => () => {
    this.setState(prev => {
      const current = prev.selectedDiscussionIds;
      const next = current.includes(id)
        ? current.filter(d => d !== id)
        : [...current, id];
      return { selectedDiscussionIds: next };
    });
  }

  toggleSelectAll = selectableIds => () => {
    this.setState(prev => {
      const allSelected = selectableIds.every(id => prev.selectedDiscussionIds.includes(id));
      return {
        selectedDiscussionIds: allSelected
          ? prev.selectedDiscussionIds.filter(id => !selectableIds.includes(id))
          : _.uniq([...prev.selectedDiscussionIds, ...selectableIds]),
      };
    });
  }

  onTestEmailChange = (e, { value }) => this.setState({ testEmail: value });

  // Previews the email to a single address with a placeholder code — no real
  // code is consumed and no discussion is touched (backend isTest path).
  handleSendTest = async () => {
    if (this._sendingTest) return;
    this._sendingTest = true;
    this.setState({ isSendingTest: true, testResult: null });
    const { campaignId } = this.props;
    const testEmail = _.trim(this.state.testEmail);
    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    try {
      if (!url) throw new Error('REACT_APP_AD_HOC_EMAIL_URL is not configured.');
      const token = authService.getToken();
      await axios.post(`${url}/discount-codes`, { campaignId, isTest: true, testEmail }, {
        timeout: 30000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (this._isMounted) this.setState({ isSendingTest: false, testResult: { type: 'success', sentTo: testEmail } });
    } catch (err) {
      if (this._isMounted) {
        this.setState({
          isSendingTest: false,
          testResult: { type: 'error', message: _.get(err, 'response.data.error') || err.message || 'Test send failed' },
        });
      }
    } finally {
      this._sendingTest = false;
    }
  }

  // First click opens the confirm step; the actual POST waits for "Yes".
  handleSendClick = () => {
    if (this._submitting) return;
    this.setState({ confirmingSend: true });
  }

  handleCancelConfirm = () => this.setState({ confirmingSend: false });

  handleSend = async recipients => {
    if (this._submitting) return;
    this._submitting = true;
    this.setState({ isSubmitting: true, submitError: null });
    const { campaignId } = this.props;
    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    const submittedIds = recipients.map(r => r.discussionId);
    try {
      if (!url) throw new Error('REACT_APP_AD_HOC_EMAIL_URL is not configured.');
      const token = authService.getToken();
      await axios.post(`${url}/discount-codes`, { campaignId, recipients }, {
        timeout: 30000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (this._isMounted) {
        this.setState({ isSubmitting: false, confirmingSend: false, submittedIds, selectedDiscussionIds: [] });
        this.startPolling();
      }
    } catch (err) {
      if (this._isMounted) {
        this.setState({
          isSubmitting: false,
          confirmingSend: false,
          submitError: _.get(err, 'response.data.error') || err.message || 'Send failed',
        });
      }
    } finally {
      this._submitting = false;
    }
  }

  renderRow = ({ recipient, state }) => {
    const { discussionId, name, email } = recipient;
    const { selectedDiscussionIds } = this.state;
    const label = `${name || '(no name)'}${email ? ` <${email}>` : ' (no email)'}`;
    const rowStyle = { padding: '5px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };

    if (state.sent) {
      return (
        <div key={discussionId} style={rowStyle}>
          <span style={{ color: '#888' }}>{label}</span>
          <Label size="tiny" color="green" basic>
            <Icon name="check" />Code sent{state.code ? ` · ${state.code}` : ''}
          </Label>
        </div>
      );
    }
    if (state.sending) {
      return (
        <div key={discussionId} style={rowStyle}>
          <span>{label}</span>
          <Label size="tiny" color="blue" basic>
            <Loader active inline size="tiny" style={{ marginRight: 6 }} />Sending…
          </Label>
        </div>
      );
    }
    if (state.unresolved) {
      return (
        <div key={discussionId} style={rowStyle}>
          <span>{label}</span>
          <Label size="tiny" color="orange" basic>Not confirmed — check back</Label>
        </div>
      );
    }
    return (
      <div key={discussionId} style={{ padding: '5px 0' }}>
        <Checkbox
          disabled={!state.sendable}
          label={label}
          checked={selectedDiscussionIds.includes(discussionId)}
          onChange={this.toggleDiscussionId(discussionId)}
        />
      </div>
    );
  }

  render() {
    const { campaignName, productCode, discussions, onClose } = this.props;
    const {
      isLoadingCount, availableCount, countError, templateName,
      statusById, selectedDiscussionIds, activeTab, filterTerm,
      isSubmitting, confirmingSend, submitError, submittedIds, sendPhase,
      testEmail, isSendingTest, testResult,
    } = this.state;

    // One row-state pass — everything below derives from `rows`.
    const submittedSet = new Set(submittedIds);
    const rows = discussions.map(d => {
      const recipient = getRecipient(d);
      return { recipient, state: computeRowState(recipient, statusById, submittedSet, sendPhase) };
    });

    // Two tabs: still-to-send (selectable / in-flight) vs already sent.
    const sentRows = rows.filter(({ state }) => state.sent);
    const toSendRows = rows.filter(({ state }) => !state.sent);
    const tabRows = activeTab === 'sent' ? sentRows : toSendRows;

    const term = _.trim(filterTerm).toLowerCase();
    const filteredRows = term
      ? tabRows.filter(({ recipient }) => (recipient.name || '').toLowerCase().includes(term)
        || (recipient.email || '').toLowerCase().includes(term))
      : tabRows;

    // Select-all only applies within the "To send" tab.
    const selectableIds = (activeTab === 'sent' ? [] : filteredRows)
      .filter(({ state }) => state.sendable && !state.sent && !state.sending)
      .map(({ recipient }) => recipient.discussionId);
    const sentCount = sentRows.length;
    const sendingCount = rows.filter(({ state }) => state.sending).length;
    const selectedCount = selectedDiscussionIds.length;
    // From all rows, not just the filtered view — a filter must never silently
    // drop an already-selected recipient from the send.
    const selectedRecipients = rows
      .filter(({ recipient }) => selectedDiscussionIds.includes(recipient.discussionId))
      .map(({ recipient }) => recipient);
    const notEnoughCodes = availableCount !== null && selectedCount > availableCount;
    const canSend = selectedCount > 0 && !notEnoughCodes && !isSubmitting && availableCount > 0;
    const hasTestEmail = isValidEmail(testEmail);
    const canSendTest = hasTestEmail && !isSendingTest && !isSubmitting;

    // Progress of the in-flight batch (confirmed by the API vs submitted).
    const batchTotal = submittedIds.length;
    const batchConfirmed = submittedIds.filter(id => _.get(statusById, [id, 'sent'])).length;
    const showProgress = sendPhase !== 'idle' && batchTotal > 0;
    const unconfirmed = sendPhase === 'timedout' && batchConfirmed < batchTotal;

    return (
      <Modal open size="large" onClose={onClose} closeOnEscape closeOnDimmerClick={false}>
        <Modal.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div>Send discount codes</div>
              <div className="small light" style={{ fontWeight: 'normal', fontSize: 13, marginTop: 2 }}>
                Campaign · {campaignName || '—'}{productCode ? ` · ${productCode}` : ''}
              </div>
              {templateName && (
                <div className="small light" style={{ fontWeight: 'normal', fontSize: 12, marginTop: 2, color: '#999' }}>
                  <Icon name="mail outline" style={{ marginRight: 4 }} />Email template: {templateName}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {productCode && (
                <div style={{ fontWeight: 'normal', fontSize: 13, textAlign: 'right', marginRight: 16 }}>
                  {isLoadingCount && availableCount === null && (
                    <span className="small light"><Loader active inline size="tiny" /> checking codes…</span>
                  )}
                  {!isLoadingCount && countError && (
                    <span style={{ color: '#9f3a38' }}>codes unavailable</span>
                  )}
                  {!countError && availableCount !== null && (
                    <span>
                      <span style={{ color: availableCount > 0 ? '#1e6f3a' : '#9f3a38', fontWeight: 700, fontSize: 16 }}>
                        {availableCount}
                      </span>{' '}available
                      {sentCount > 0 && <span className="small light"> · {sentCount} sent</span>}
                      {sendingCount > 0 && <span style={{ color: '#2185d0' }}> · {sendingCount} sending</span>}
                    </span>
                  )}
                </div>
              )}
              <Icon name="close" link onClick={onClose} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          {!productCode && (
            <Message size="tiny" warning>
              <Icon name="warning sign" />
              No product code set on this campaign.
            </Message>
          )}
          {productCode && (
            <Form>
              {showProgress && (
                <Progress
                  value={batchConfirmed}
                  total={batchTotal}
                  progress="ratio"
                  autoSuccess
                  active={sendPhase === 'sending'}
                  color={sendPhase === 'sending' ? 'blue' : 'green'}
                  size="small"
                >
                  {sendPhase === 'sending' ? 'Sending codes — confirmed as each one lands' : 'Send complete'}
                </Progress>
              )}

              <Form.Field>
                <Button.Group basic fluid style={{ marginBottom: 8 }}>
                  <Button
                    type="button"
                    active={activeTab === 'tosend'}
                    style={tabStyle(activeTab === 'tosend')}
                    onClick={this.setTab('tosend')}
                  >
                    To send ({toSendRows.length})
                  </Button>
                  <Button
                    type="button"
                    active={activeTab === 'sent'}
                    style={tabStyle(activeTab === 'sent')}
                    onClick={this.setTab('sent')}
                  >
                    Sent ({sentRows.length})
                  </Button>
                </Button.Group>
                <Input
                  fluid
                  icon="search"
                  placeholder="Filter by name or email…"
                  value={filterTerm}
                  onChange={this.onFilterChange}
                />
              </Form.Field>

              {selectableIds.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <Checkbox
                    label={`Select all sendable (${selectableIds.length})`}
                    checked={selectableIds.every(id => selectedDiscussionIds.includes(id))}
                    onChange={this.toggleSelectAll(selectableIds)}
                  />
                </div>
              )}

              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {_.isEmpty(filteredRows) && (
                  <p className="small light">
                    {(() => {
                      if (discussions.length === 0) return 'No collaborations on this campaign yet.';
                      if (term) return 'No matches for that filter.';
                      return activeTab === 'sent' ? 'No codes sent yet.' : 'Everyone already has a code.';
                    })()}
                  </p>
                )}
                {filteredRows.map(this.renderRow)}
              </div>

              {notEnoughCodes && (
                <Message size="tiny" warning>
                  <Icon name="warning sign" />
                  Only {availableCount} code{availableCount === 1 ? '' : 's'} available — you've selected {selectedCount}.
                </Message>
              )}
              {availableCount === 0 && (
                <Message size="tiny" warning>
                  <Icon name="warning sign" />
                  No codes available for this product. Load more before sending.
                </Message>
              )}
              {unconfirmed && (
                <Message size="tiny" warning onDismiss={() => this.setState({ sendPhase: 'idle', submittedIds: [] })}>
                  <Icon name="clock outline" />
                  {batchTotal - batchConfirmed} code{batchTotal - batchConfirmed === 1 ? '' : 's'} not confirmed yet — reopen this modal shortly to check.
                </Message>
              )}
              {submitError && (
                <Message size="tiny" negative onDismiss={() => this.setState({ submitError: null })}>
                  <Icon name="exclamation circle" />{submitError}
                </Message>
              )}
              {testResult && testResult.type === 'success' && (
                <Message size="tiny" positive onDismiss={() => this.setState({ testResult: null })}>
                  <Icon name="paper plane outline" />
                  Test email sent to {testResult.sentTo} (placeholder code — no real code used).
                </Message>
              )}
              {testResult && testResult.type === 'error' && (
                <Message size="tiny" negative onDismiss={() => this.setState({ testResult: null })}>
                  <Icon name="exclamation circle" />{testResult.message}
                </Message>
              )}
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          {confirmingSend ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#666', textAlign: 'left' }}>
                <Icon name="question circle outline" />
                Send {selectedCount} discount code{selectedCount === 1 ? '' : 's'}? Codes are allocated and emailed immediately — this can&apos;t be undone.
              </span>
              <div style={{ whiteSpace: 'nowrap' }}>
                <Button onClick={this.handleCancelConfirm} disabled={isSubmitting}>No, go back</Button>
                <Button
                  color="pink"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => this.handleSend(selectedRecipients)}
                >
                  Yes, send {selectedCount} code{selectedCount === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input
                  size="small"
                  type="email"
                  placeholder="Test recipient email"
                  value={testEmail}
                  onChange={this.onTestEmailChange}
                  error={!!_.trim(testEmail) && !hasTestEmail}
                  style={{ minWidth: 230 }}
                />
                <Button
                  type="button"
                  loading={isSendingTest}
                  disabled={!canSendTest}
                  onClick={this.handleSendTest}
                  style={{ background: 'transparent', color: '#2185d0', boxShadow: 'none', fontWeight: 'normal', padding: '8px 0' }}
                >
                  Send test
                </Button>
              </div>
              <div>
                <Button onClick={onClose} disabled={isSubmitting}>Close</Button>
                <Button
                  color="pink"
                  disabled={!canSend}
                  onClick={this.handleSendClick}
                >
                  Send {selectedCount > 0 ? `${selectedCount} ` : ''}code{selectedCount === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

SendDiscountCodesModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  productCode: PropTypes.string,
  discussions: PropTypes.array,
  currentUserEmail: PropTypes.string,
  restClient: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

SendDiscountCodesModal.defaultProps = {
  campaignName: '',
  productCode: '',
  discussions: [],
  currentUserEmail: '',
};

export default compose(
  hasRESTClient,
)(SendDiscountCodesModal);



/** WEBPACK FOOTER **
 ** ./src/components/send-discount-codes-modal/index.js
 **/